import { ApiError, parseApiError } from "./error.js";
/** Guard が「アクセストークン期限切れ」を示すために返すエラーコード。 */
const TOKEN_EXPIRED_CODE = "TOKEN_EXPIRED";
export class ApiClient {
    baseUrl;
    onUnauthorized;
    refresh;
    requestIdFactory;
    fetchFn;
    /** 進行中の refresh を共有して多重実行を防ぐ（single-flight）。 */
    refreshInFlight = null;
    constructor(opts = {}) {
        this.baseUrl = opts.baseUrl ?? "";
        this.onUnauthorized = opts.onUnauthorized;
        this.refresh = opts.refresh;
        this.requestIdFactory = opts.requestIdFactory ?? defaultRequestId;
        this.fetchFn = opts.fetch ?? fetch.bind(globalThis);
    }
    async get(path, opts = {}) {
        return this.request("GET", path, opts);
    }
    async post(path, opts = {}) {
        return this.request("POST", path, opts);
    }
    async patch(path, opts = {}) {
        return this.request("PATCH", path, opts);
    }
    async put(path, opts = {}) {
        return this.request("PUT", path, opts);
    }
    async delete(path, opts = {}) {
        return this.request("DELETE", path, opts);
    }
    async upload(path, formData, opts = {}, isRetry = false) {
        const { query, idempotencyKey, headers, ...init } = opts;
        const url = this.buildUrl(path, query);
        const reqHeaders = new Headers(headers);
        reqHeaders.set("x-request-id", this.requestIdFactory());
        if (idempotencyKey) {
            reqHeaders.set("Idempotency-Key", idempotencyKey);
        }
        else {
            reqHeaders.set("Idempotency-Key", this.requestIdFactory());
        }
        let res;
        try {
            res = await this.fetchFn(url, {
                ...init,
                method: "POST",
                credentials: init.credentials ?? "include",
                headers: reqHeaders,
                body: formData,
            });
        }
        catch (err) {
            throw new ApiError({
                status: 0,
                code: "NETWORK_ERROR",
                message: err instanceof Error ? err.message : "ネットワークに接続できません",
            });
        }
        if (res.status === 401) {
            if (!isRetry && this.refresh && (await this.isTokenExpired(res))) {
                const refreshed = await this.runRefresh();
                if (refreshed) {
                    return this.upload(path, formData, opts, true);
                }
            }
            this.onUnauthorized?.();
        }
        if (!res.ok) {
            throw await parseApiError(res);
        }
        if (res.status === 204)
            return undefined;
        const contentType = res.headers.get("content-type");
        if (!contentType?.includes("application/json"))
            return undefined;
        return (await res.json());
    }
    async request(method, path, opts, isRetry = false) {
        const { body, query, idempotencyKey, headers, ...init } = opts;
        const url = this.buildUrl(path, query);
        const reqHeaders = new Headers(headers);
        reqHeaders.set("x-request-id", this.requestIdFactory());
        if (body !== undefined) {
            reqHeaders.set("content-type", "application/json");
        }
        if (idempotencyKey && (method === "POST" || method === "PATCH" || method === "PUT")) {
            reqHeaders.set("Idempotency-Key", idempotencyKey);
        }
        else if (method === "POST" || method === "PATCH" || method === "PUT") {
            // 全 write 系に自動で Idempotency-Key を付与（明示指定が無い場合）
            reqHeaders.set("Idempotency-Key", this.requestIdFactory());
        }
        let res;
        try {
            res = await this.fetchFn(url, {
                ...init,
                method,
                credentials: init.credentials ?? "include",
                headers: reqHeaders,
                body: body === undefined ? undefined : JSON.stringify(body),
            });
        }
        catch (err) {
            throw new ApiError({
                status: 0,
                code: "NETWORK_ERROR",
                message: err instanceof Error ? err.message : "ネットワークに接続できません",
            });
        }
        if (res.status === 401) {
            // アクセストークン期限切れ → 一度だけ refresh して元リクエストを再試行する。
            // 再試行後やそれ以外の 401（未ログイン等）は onUnauthorized へフォールバック。
            if (!isRetry && this.refresh && (await this.isTokenExpired(res))) {
                const refreshed = await this.runRefresh();
                if (refreshed) {
                    return this.request(method, path, opts, true);
                }
            }
            this.onUnauthorized?.();
        }
        if (!res.ok) {
            throw await parseApiError(res);
        }
        if (res.status === 204) {
            return undefined;
        }
        const contentType = res.headers.get("content-type");
        if (!contentType?.includes("application/json")) {
            return undefined;
        }
        return (await res.json());
    }
    /** レスポンス本文を覗き見て TOKEN_EXPIRED か判定する（本文は clone して消費しない）。 */
    async isTokenExpired(res) {
        try {
            const body = (await res.clone().json());
            return body?.error?.code === TOKEN_EXPIRED_CODE;
        }
        catch {
            return false;
        }
    }
    /** refresh を single-flight で実行する（同時 401 を 1 回の refresh に集約）。 */
    runRefresh() {
        if (!this.refresh)
            return Promise.resolve(false);
        this.refreshInFlight ??= Promise.resolve(this.refresh()).finally(() => {
            this.refreshInFlight = null;
        });
        return this.refreshInFlight;
    }
    buildUrl(path, query) {
        const base = `${this.baseUrl}${path}`;
        if (!query)
            return base;
        const params = new URLSearchParams();
        for (const [k, v] of Object.entries(query)) {
            if (v === undefined || v === null || v === "")
                continue;
            if (Array.isArray(v)) {
                for (const item of v) {
                    if (item !== undefined && item !== null && item !== "") {
                        params.append(k, String(item));
                    }
                }
            }
            else {
                params.set(k, String(v));
            }
        }
        const qs = params.toString();
        return qs ? `${base}?${qs}` : base;
    }
}
function defaultRequestId() {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
        return crypto.randomUUID();
    }
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
}
